import { useEffect, useState } from 'react';

interface LoadingPopupProps {
  isOpen: boolean;
  contentType?: 'social-media-post' | 'cold-email' | 'linkedin-dm';
}

const LoadingPopup = ({ isOpen, contentType = 'cold-email' }: LoadingPopupProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const stepsByType = {
    'social-media-post': [
      'Analyzing platform-specific best practices...',
      'Generating content strategy...',
      'Crafting engaging post content...',
      'Optimizing hashtags and keywords...',
      'Finalizing content...'
    ],
    'cold-email': [
      'Researching industry trends and insights...',
      'Analyzing target audience preferences...',
      'Drafting email campaign sequence...',
      'Running quality checks...',
      'Finalizing content...'
    ],
    'linkedin-dm': [
      'Researching industry insights...',
      'Analyzing LinkedIn engagement patterns...',
      'Crafting connection request...',
      'Generating follow-up sequence...',
      'Finalizing content...'
    ]
  };

  // Get steps for current content type, defaulting to cold-email steps if type is invalid
  const steps = stepsByType[contentType] || stepsByType['cold-email'];

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < steps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [isOpen, steps]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-background border border-secondary/20 rounded-xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center">
          {/* Loading spinner */}
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
          
          {/* Steps */}
          <div className="w-full space-y-4">
            {steps.map((step, index) => (
              <div
                key={step}
                className={`flex items-center gap-3 transition-opacity duration-500 ${
                  index <= currentStep ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <div className={`w-4 h-4 rounded-full ${
                  index < currentStep ? 'bg-primary' :
                  index === currentStep ? 'bg-primary animate-pulse' :
                  'bg-secondary/20'
                }`}></div>
                <span className="text-text-primary">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingPopup; 